import { Button, TextField, useAlert } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CityViewModel } from '@/shared/api/generated';

import { useSaveCity } from '../../../hooks/useSaveCity';
import { type CityFormValues, cityFormSchema } from '../../../types/cityForm';
import { StateAutocomplete } from '../StateAutocomplete';

export interface CityFormModalProps {
  readonly open: boolean;
  /** Cidade em edição; `null` abre o modal em modo de criação. */
  readonly city: CityViewModel | null;
  readonly onOpenChange: (open: boolean) => void;
}

const DEFAULT_VALUES: CityFormValues = { code: '', name: '', stateId: 0, population: undefined };

/**
 * Modal de criação/edição de Cidade (Radix Dialog + React Hook Form + Zod).
 *
 * Um único formulário cobre criar (campos vazios) e editar (pré-preenchido a
 * partir da linha, incluindo o Estado via `stateId`/`stateName` e a População).
 * O Estado é escolhido num `StateAutocomplete` (busca client-side). Persiste via
 * `useSaveCity` e mapeia o conflito **409** (código já cadastrado) para erro no
 * campo Código, mantendo o modal aberto. Fiel aos frames *Cidades — Modal:
 * Adicionar/Editar* de `design/location.pen`.
 */
export function CityFormModal({ open, city, onOpenChange }: CityFormModalProps) {
  const { t } = useTranslation('location');
  const { alert } = useAlert();
  const { save, isPending } = useSaveCity();
  const [hasConflict, setHasConflict] = useState(false);

  const isEdit = city?.id != null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CityFormValues>({
    resolver: zodResolver(cityFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  // Sincroniza o formulário sempre que o modal abre (com a cidade em edição ou vazio).
  useEffect(() => {
    if (!open) return;
    setHasConflict(false);
    reset(
      city
        ? {
            code: city.code ?? '',
            name: city.name ?? '',
            stateId: city.stateId ?? 0,
            population: city.population,
          }
        : DEFAULT_VALUES,
    );
  }, [open, city, reset]);

  const onSubmit = handleSubmit(values => {
    setHasConflict(false);
    save(values, city?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(isEdit ? 'cities.toast.updateSuccess' : 'cities.toast.createSuccess'),
        });
        onOpenChange(false);
      },
      onConflict: () => setHasConflict(true),
      onError: () => alert({ alertType: 'warning', title: t('cities.toast.saveError') }),
    });
  });

  const codeHelper = errors.code
    ? t('cities.form.errors.codeRequired')
    : hasConflict
      ? t('cities.form.errors.conflict')
      : undefined;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay.
            `modal={false}` é necessário para o dropdown do Autocomplete de Estado (portado
            ao body) permanecer clicável. Ver reference_ads_autocomplete_selecteditem. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          aria-describedby={undefined}
          onInteractOutside={e => e.preventDefault()}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex w-full max-w-[36rem] flex-col gap-xxs rounded-lg bg-ctx-layout-surface p-xxs shadow-level4 focus:outline-none"
        >
          <Dialog.Title className="text-ctx-content-title ts-heading-xs">
            {t(isEdit ? 'cities.form.editTitle' : 'cities.form.createTitle')}
          </Dialog.Title>

          <form className="flex flex-col gap-mini" onSubmit={onSubmit} noValidate>
            <div className="grid grid-cols-1 gap-mini sm:grid-cols-[150px_1fr]">
              <Controller
                control={control}
                name="code"
                render={({ field }) => (
                  <TextField
                    label={`${t('cities.form.code')}*`}
                    placeholder={t('cities.form.codePlaceholder')}
                    value={field.value}
                    onChange={e => {
                      setHasConflict(false);
                      field.onChange(e.target.value);
                    }}
                    size="md"
                    fullWidth
                    hasError={Boolean(errors.code) || hasConflict}
                    {...(codeHelper ? { helper: codeHelper } : {})}
                  />
                )}
              />

              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    label={`${t('cities.form.name')}*`}
                    placeholder={t('cities.form.namePlaceholder')}
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    size="md"
                    fullWidth
                    hasError={Boolean(errors.name)}
                    {...(errors.name ? { helper: t('cities.form.errors.nameRequired') } : {})}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-mini sm:grid-cols-[1fr_200px]">
              <Controller
                control={control}
                name="stateId"
                render={({ field }) => (
                  <div className="flex flex-col gap-quark">
                    <StateAutocomplete
                      label={`${t('cities.form.state')}*`}
                      placeholder={t('cities.form.statePlaceholder')}
                      value={field.value}
                      selectedLabel={city?.stateName ?? ''}
                      onChange={id => field.onChange(id)}
                      hasError={Boolean(errors.stateId)}
                    />
                    {errors.stateId ? (
                      <p className="text-base-danger-600 ts-caption-xxs">
                        {t('cities.form.errors.stateRequired')}
                      </p>
                    ) : null}
                  </div>
                )}
              />

              <Controller
                control={control}
                name="population"
                render={({ field }) => (
                  <TextField
                    label={t('cities.form.population')}
                    placeholder={t('cities.form.populationPlaceholder')}
                    value={field.value != null ? String(field.value) : ''}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '');
                      field.onChange(digits === '' ? undefined : Number(digits));
                    }}
                    size="md"
                    fullWidth
                  />
                )}
              />
            </div>

            <div className="mt-nano flex justify-end gap-nano">
              <Dialog.Close asChild>
                <Button hierarchy="secondary" size="md" type="button">
                  {t('cities.form.cancel')}
                </Button>
              </Dialog.Close>
              <Button
                hierarchy="primary"
                size="md"
                type="submit"
                isLoading={isPending}
                disabled={isPending}
              >
                {t('cities.form.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
